import React from 'react';
import { MenuItem, Select, Divider, ListItemText, ListItemIcon } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { Control, Controller, useWatch } from 'react-hook-form';
import {
  CheckBoxOutlined as Checkbox,
  Event as Date,
  RadioButtonCheckedOutlined as Radio,
  Room,
  ShortText,
  Subject as Textarea,
  AttachFile,
  PhotoAlbum as Picture,
  AccountBalance,
  Phone,
} from '@mui/icons-material';

import {
  FormQuestionType,
  ContextType,
  singletonQuestionTypes,
  alwaysPublicTypes,
  questionTypesForContext,
} from '../../../../../../Utils/Types/FormBuilder';
import useConfirmationDialog from '../../../../../../Hooks/useConfirmationDialog';
import { confirmChangeType } from '../../../../../../Utils/Post/Form/questionUpdateWarning';

type TypeSelectProps = {
  scope?: string;
  position: number;
  control: Control<any>;
  context: ContextType;
  setValue: (name: string, value: any) => void;
};

export const icons: { [key in FormQuestionType]: JSX.Element } = {
  attachment: <AttachFile color="action" />,
  checkbox: <Checkbox color="action" />,
  date: <Date color="action" />,
  location: <Room color="action" />,
  money: <AccountBalance color="action" />,
  phone_number: <Phone color="action" />,
  picture: <Picture color="action" />,
  radio: <Radio color="action" />,
  text: <ShortText color="action" />,
  textarea: <Textarea color="action" />,
};

function isDisabledOption(type: FormQuestionType, questionsAttributes: any, position: number) {
  if (singletonQuestionTypes.includes(type)) {
    return questionsAttributes.some(
      (question: any) =>
        question.field_type === type &&
        question !== questionsAttributes[position] &&
        !question._destroy,
    );
  }

  return false;
}

const DELEGATABLE_TYPES = [
  {
    field_type: 'money',
    post_record_attribute_key: 'budget',
  },
];

function TypeSelect({
  scope = 'form_questions_attributes',
  control,
  position,
  context,
  setValue,
}: TypeSelectProps) {
  const intl = useIntl();
  const questionsAttributes = useWatch({
    control,
    name: scope,
  });
  const { confirm, dialog: confirmationDialog } = useConfirmationDialog('form-builder');

  const handleTypeChange = async (e: any, field: any) => {
    if (questionsAttributes[position]?.answers_count > 0) {
      const confirmedChangeType = await confirmChangeType({ confirm, intl });
      if (!confirmedChangeType) {
        return false;
      }
    }

    if (alwaysPublicTypes.includes(e.target.value)) {
      setValue(`${scope}.${position}.confidential`, false);
    }

    // clear question options if the type is not radio or checkbox
    if (!['radio', 'checkbox'].includes(e.target.value)) {
      setValue(`${scope}.${position}.question_options_attributes`, []);
    }

    // Other questions (like post title and content) that need to be delegated while being of the basic question field_types
    // should set the post_record_attribute_key in the default values
    const delegatableType = DELEGATABLE_TYPES.find((type) => type.field_type === e.target.value);

    setValue(`${scope}.${position}.accepted_answers`, null);
    setValue(
      `${scope}.${position}.post_record_attribute_key`,
      delegatableType?.post_record_attribute_key || null,
    );

    field.onChange(e);
  };

  return (
    <>
      <Controller
        name={`${scope}.${position}.field_type`}
        control={control}
        defaultValue="text"
        render={({ field }) => (
          <Select
            {...field}
            fullWidth
            variant="outlined"
            onChange={(e: any) => handleTypeChange(e, field)}
            inputProps={{
              sx: {
                display: 'flex',
                alignItems: 'center',
                '& .MuiListItemIcon-root': {
                  minWidth: 40,
                },
                p: 1.4,
              },
            }}
          >
            {questionTypesForContext[context].map(
              (type: FormQuestionType | 'divider', typeIndex: number) => {
                if (type === 'divider') {
                  return <Divider key={`${type}-${typeIndex}`} />;
                }

                return (
                  <MenuItem
                    key={type}
                    value={type}
                    sx={{ py: 1.5 }}
                    disabled={isDisabledOption(type, questionsAttributes, position)}
                  >
                    <ListItemIcon>{icons[type]}</ListItemIcon>
                    <ListItemText>
                      <FormattedMessage id={`POST_FORM.FORM_BUILDER.TYPES.${type.toUpperCase()}`} />
                    </ListItemText>
                  </MenuItem>
                );
              },
            )}
          </Select>
        )}
      />
      {confirmationDialog}
    </>
  );
}

export default TypeSelect;
